import React from "react";
import Select, { MultiValue } from "react-select";
import { Box, FlexBox, Option } from "../primitives";
import {
  StyledSelect,
  StyledInput,
  TitleStyle,
  LabelStyled,
} from "../common/common.style";
import {
  CARD_CONDITIONS_CARDTRADER,
  LANGUAGE_FOR_DROPDOWN,
} from "../../constant";
import { MultiSelectConditionType, MultiSelectLanguageType } from "../../type";

interface FilterProps {
  filterOption: string;
  sortOption: string;
  searchText: string;
  setFilterOption: React.Dispatch<React.SetStateAction<string>>;
  setSearchText: React.Dispatch<React.SetStateAction<string>>;
  setSortOption: React.Dispatch<React.SetStateAction<string>>;
  priceMin?: number;
  priceMax?: number;
  setPriceMin: (arg1: number) => void;
  setPriceMax: (arg1: number) => void;
  setLanguagesFilter: (arg1: MultiSelectLanguageType[]) => void;
  setConditionsFilter: (arg1: MultiSelectConditionType[]) => void;
  languagesFilter: MultiSelectLanguageType[];
  conditionsFilter: MultiSelectConditionType[];
  setSearchTextComment: (arg1: string) => void;
  searchTextComment: string;
}
export const FilterV2 = ({
  setSortOption,
  sortOption,
  searchText,
  filterOption = "none",
  setSearchText,
  setFilterOption,
  priceMin,
  priceMax,
  setPriceMin,
  setPriceMax,
  setLanguagesFilter,
  setConditionsFilter,
  languagesFilter,
  conditionsFilter,
  setSearchTextComment,
  searchTextComment,
}: FilterProps) => {
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const handleSortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSortOption(e.target.value);
  };

  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFilterOption?.(e.target.value);
  };

  const handleMinPriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value);
    setPriceMin?.(isNaN(value) ? 0 : value);
  };

  const handleMaxPriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value);
    setPriceMax?.(isNaN(value) ? Infinity : value);
  };

  const mappedLanguages = LANGUAGE_FOR_DROPDOWN.map((language) => ({
    value: language.value,
    label: language.label,
  }));

  const mappedCondition = CARD_CONDITIONS_CARDTRADER.map((condition) => ({
    value: condition.value,
    label: condition.value,
  }));

  const handleChangeLanguage = (value: MultiValue<MultiSelectLanguageType>) => {
    setLanguagesFilter([...value] as MultiSelectLanguageType[]);
  };

  const handleChangeCondition = (
    value: MultiValue<MultiSelectConditionType>
  ) => {
    setConditionsFilter([...value] as MultiSelectConditionType[]);
  };

  return (
    <FlexBox
      css={{
        marginTop: 20,
        borderTop: "1px solid #cbd5e1",
        borderBottom: "1px solid #cbd5e1",
        padding: "10px 0px",
        gap: 12,
        flexWrap: "wrap",
        flexDirection: "column",
      }}
    >
      <TitleStyle>FILTERS</TitleStyle>

      <StyledInput
        type="text"
        placeholder="Search by name, edition code, or number"
        value={searchText}
        onChange={handleSearchChange}
      />
      <Select
        options={mappedLanguages}
        isMulti
        value={languagesFilter}
        onChange={handleChangeLanguage}
        placeholder="Choose language..."
        styles={{
          container: (base) => ({
            ...base,
            width: "100%",
          }),
        }}
      />
      <Select
        options={mappedCondition}
        isMulti
        value={conditionsFilter}
        onChange={handleChangeCondition}
        placeholder="Choose condition..."
        styles={{
          container: (base) => ({
            ...base,
            width: "100%",
          }),
        }}
      />
      <Box>
        <LabelStyled>Sorting</LabelStyled>
        <StyledSelect
          value={sortOption}
          onChange={handleSortChange}
          css={{ marginTop: 5 }}
        >
          <Option value="name-asc">Name Ascending</Option>
          <Option value="name-desc">Name Descending</Option>
          <Option value="price-asc">Price Ascending</Option>
          <Option value="price-desc">Price Descending</Option>
          <Option value="price-diff-asc">Price Difference Ascending</Option>
          <Option value="price-diff-desc">Price Difference Descending</Option>
          <Option value="color">By color</Option>
          <Option value="date-asc">Insert Ascending</Option>
          <Option value="date-desc">Insert Descending</Option>
        </StyledSelect>
      </Box>
      <Box>
        <LabelStyled>Filtering</LabelStyled>
        <StyledSelect
          value={filterOption || "none"}
          onChange={handleFilterChange}
          css={{ marginTop: 5 }}
        >
          <Option value="none">All cards</Option>
          <Option value="only-edited">Only Edited</Option>
          <Option value="not-edited">Not Edited</Option>
          <Option value="pricing-error">Pricing Error</Option>
        </StyledSelect>
      </Box>
      <StyledInput
        type="text"
        placeholder="Search by comments"
        value={searchTextComment}
        onChange={(e) => {
          setSearchTextComment(e.target.value);
        }}
      />

      <FlexBox css={{ gap: 10 }}>
        <StyledInput
          type="number"
          placeholder="Min Price"
          value={priceMin ?? ""}
          onChange={handleMinPriceChange}
          css={{ flexGrow: 1 }}
          onKeyDown={(e) => {
            if (["e", "E", "+", "-"].includes(e.key)) {
              e.preventDefault();
            }
          }}
        />
        <StyledInput
          type="number"
          placeholder="Max Price"
          value={priceMax ?? ""}
          onChange={handleMaxPriceChange}
          css={{ flexGrow: 1 }}
          onKeyDown={(e) => {
            if (["e", "E", "+", "-"].includes(e.key)) {
              e.preventDefault();
            }
          }}
        />
      </FlexBox>
    </FlexBox>
  );
};
